import { makeStyles, MenuItem, Theme } from "@material-ui/core";
import { FilterList } from "@material-ui/icons";
import React, { useMemo } from "react";
import {
  FilterCheckbox,
  FilterMenu,
  FilterSearch,
  Filter,
  FilterRadioButton,
} from "@ofqual/portal-components/components/ResponseFilter";
import {
  useAuth,
  useLookup,
  useSearch,
  useUrl,
} from "@ofqual/portal-core/core/hooks";
import { Select } from "@ofqual/portal-components/components/Select";
import {
  aoService,
  aoServiceSearch,
  counselCategoryLookupService,
  directorateLookupService,
  statusService,
  usersService,
} from "../../services";
import { UserRole } from "../../redux/types";
import { authSelector } from "../../redux/selectors";
import { ensureParamIsArray } from "@ofqual/portal-core/core/utils/urlHelper";
import { useSelector } from "react-redux";
import { SearchField as ClassicSearchField } from "@ofqual/portal-components/components/SearchField";
import { fieldLabels } from "../sections/fieldlabels";
import { QueryParams } from "@ofqual/portal-core/core/types";
import { keywordService } from "../../services";

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      "& fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    menuItem: {
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
  };
});

export const FilterControls: React.FC = () => {
  const classes = useStyles();
  const { userType } = useAuth(authSelector);
  const { roles } = useSelector(authSelector);
  const { token } = useSelector(authSelector);

  const [
    statusLookups,
    userLookups,
    counselCategoryLookups,
    directorateLookups,
  ] = useLookup(
    statusService,
    {
      service: usersService,
      id: "upn",
      label: (item) => (item.fullName ? item.fullName : item.upn),
      params: {
        userType,
        role: [
          UserRole.Director,
          UserRole.CaseManager,
          UserRole.GC,
          UserRole.LPM,
        ],
        active: true,
      },
    },
    counselCategoryLookupService,
    directorateLookupService
  );

  const { searchParams, navigateToNextSearch, searchParamsFiltersOnly } =
    useSearch(useUrl());

  const mappedKeywordLookupService = useMemo(
    () => ({
      get: (id: string) => keywordService.get(id.toString(), { token }),
      search: (params?: QueryParams) =>
        keywordService.search(params || {}, { token }),
    }),
    [token]
  );

  const mappedOrgLookupService = useMemo(
    () => ({
      get: (id: string) => aoService.get(id.toString(), { token }),
      search: (params?: QueryParams) =>
        aoServiceSearch.search(params, { token }),
    }),
    [token]
  );

  return (
    <>
      <FilterSearch
        title="Search..."
        value={searchParams.search}
        onChange={(value) =>
          navigateToNextSearch({
            search: value,
          })
        }
        dataTest={"search"}
      />
      &nbsp;&nbsp;
      <Filter
        Icon={FilterList}
        title="Order By"
        badge={!!searchParams.order}
        dataTest="lcm-list-order"
      >
        <FilterRadioButton
          isChecked={typeof searchParams.order !== "string"}
          value={undefined}
          text={"Created date"}
          dataTest={"sort-created-date"}
          onChange={() =>
            navigateToNextSearch({
              order: undefined,
              orderDirection: undefined,
            })
          }
        />
        <FilterRadioButton
          isChecked={searchParams.order === "assignedCreatedDate"}
          value={"assignedCreatedDate"}
          text={"Date received"}
          dataTest={"sort-date-received"}
          onChange={() =>
            navigateToNextSearch({
              order: "assignedCreatedDate",
              orderDirection: "desc",
            })
          }
        />
        <FilterRadioButton
          isChecked={searchParams.order === "DueDate"}
          value={"DueDate"}
          text={"Due date"}
          dataTest={"sort-date-due"}
          onChange={() =>
            navigateToNextSearch({
              order: "DueDate",
              orderDirection: "desc",
            })
          }
        />
        <FilterRadioButton
          isChecked={searchParams.order === "KeyDate"}
          value={"KeyDate"}
          text={"Key Date(s)"}
          dataTest={"sort-key-date"}
          onChange={() =>
            navigateToNextSearch({
              order: "KeyDate",
              orderDirection: "desc",
            })
          }
        />
      </Filter>
      <FilterMenu
        Icon={FilterList}
        title="Filters"
        dataTest="filter"
        search={{ searchParamsFiltersOnly, navigateToNextSearch }}
      >
        {(workingParams, dirtyParams, addParam, updateMultipleParams) => (
          <form>
            <MenuItem className={classes.menuItem}>
              <Select
                className={
                  dirtyParams.includes("status") ? classes.root : undefined
                }
                label="Status"
                data-test="status-filter"
                value={workingParams.status}
                onChange={(e) => {
                  addParam("status", e.target.value);
                }}
              >
                {statusLookups}
              </Select>
            </MenuItem>
            <MenuItem>
              <ClassicSearchField
                className={
                  dirtyParams.includes("keyword") ? classes.root : undefined
                }
                onChange={(e) => addParam("keyword", e.target.value)}
                value={workingParams.keyword}
                label={fieldLabels.taggingKeywords}
                datatest="advice-keywords-filter-field"
                service={mappedKeywordLookupService}
                searchFieldVariant="multiple"
              />
            </MenuItem>
            <MenuItem className={classes.menuItem}>
              <Select
                className={
                  dirtyParams.includes("status") ? classes.root : undefined
                }
                label="Counsel Category"
                data-test="counselCategory-filter"
                value={workingParams.counselCategory}
                onChange={(e) => addParam("counselCategory", e.target.value)}
              >
                {counselCategoryLookups}
              </Select>
            </MenuItem>
            <MenuItem className={classes.menuItem}>
              <Select
                className={
                  dirtyParams.includes("assignedTo") ? classes.root : undefined
                }
                label="Assigned to"
                data-test="assigned-to-filter"
                selectVariant="multiple"
                value={ensureParamIsArray(workingParams.assignedTo)}
                onChange={(e) => addParam("assignedTo", e.target.value)}
                disabled={workingParams.unassignedOnly === "true"}
              >
                {userLookups}
              </Select>
            </MenuItem>
            <FilterCheckbox
              isChecked={workingParams.unassignedOnly === "true"}
              name="unassignedOnly"
              dataTest="unassigned-checkbox"
              text="Unassigned"
              onChange={() => {
                updateMultipleParams({
                  assignedTo: undefined,
                  unassignedOnly: workingParams.unassignedOnly
                    ? undefined
                    : "true",
                });
              }}
            />
            {roles.includes(UserRole.GC) ? (
              <FilterCheckbox
                isChecked={workingParams.sensitiveOnly === "true"}
                name="sensitiveOnly"
                dataTest="sensitiveOnly-checkbox"
                text="Sensitive"
                onChange={() =>
                  addParam(
                    "sensitiveOnly",
                    workingParams.sensitiveOnly ? undefined : "true"
                  )
                }
              />
            ) : null}
            <MenuItem className={classes.menuItem}>
              <Select
                className={
                  dirtyParams.includes("externalCounselReq")
                    ? classes.root
                    : undefined
                }
                label="External counsel requested"
                data-test="external-counsel-requested-filter"
                selectVariant="multiple"
                value={ensureParamIsArray(workingParams.externalCounselReq)}
                onChange={(e) => addParam("externalCounselReq", e.target.value)}
              >
                {[
                  { id: "0", label: "Requested" },
                  { id: "1", label: "Accepted" },
                  { id: "2", label: "Rejected" },
                ]}
              </Select>
            </MenuItem>
            <MenuItem className={classes.menuItem}>
              <ClassicSearchField
                className={
                  dirtyParams.includes("awardingOrganisation")
                    ? classes.root
                    : undefined
                }
                onChange={(e) =>
                  addParam("awardingOrganisation", e.target.value)
                }
                value={workingParams.awardingOrganisation}
                label={"Awarding Organisations"}
                datatest="filter-aos"
                service={mappedOrgLookupService}
                searchFieldVariant="multiple"
              />
            </MenuItem>
            <MenuItem className={classes.menuItem}>
              <Select
                className={
                  dirtyParams.includes("directorate") ? classes.root : undefined
                }
                label="Directorate"
                data-test="directorate-filter"
                value={ensureParamIsArray(workingParams.directorate)}
                onChange={(e) =>
                  e.target.value && addParam("directorate", e.target.value)
                }
                selectVariant="multiple"
              >
                {directorateLookups.map((x) => ({
                  id: x.id.toString(),
                  label: x.label,
                }))}
              </Select>
            </MenuItem>
          </form>
        )}
      </FilterMenu>
    </>
  );
};
